<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { ArrowRight, Check, ChevronDown, ChevronUp, Play, Sparkles } from 'lucide-vue-next'
import { appLoginUrl } from '@/config'
import UseCaseArticle from '@/components/marketing/UseCaseArticle.vue'
import { relatedUseCasesFor, useCaseBySlug } from '@/data/useCases'

// useCaseBySlug looks up the route's :slug param. Vite-ssg's includedRoutes
// only enumerates valid slugs, so during prerender this is always defined.
// On the client, an unknown slug falls through to the NotFound redirect below.
const route = useRoute()
const router = useRouter()
const SITE_URL = 'https://open-muse.com'
const SITE_NAME = 'OpenMuse'

const useCase = computed(() => useCaseBySlug(String(route.params.slug)))

onMounted(() => {
  if (!useCase.value) router.replace({ name: 'NotFound' })
})

// useHead is called inline (not via useSeo) because every field is slug-derived
// — letting Vue track the computeds keeps meta + JSON-LD correct when a user
// navigates client-side between two use-case pages without a remount.
const fullTitle = computed(() => `${useCase.value?.title ?? ''} | ${SITE_NAME}`)
const canonical = computed(() => `${SITE_URL}${route.path}`)
// Every guide has a composed 1200×630 card. Portrait pages keep the whole head
// and signature result in frame; landscape pages use the same stable frame so
// every social-image dimension is truthful and maintained by one generator.
const ogImage = computed(() => {
  const u = useCase.value
  if (!u) return `${SITE_URL}/hero/og-card-roadtrip.jpg`
  return `${SITE_URL}/social/use-cases/${u.slug}.jpg`
})
const ogImageWidth = '1200'
const ogImageHeight = '630'
const ogImageAlt = computed(() => `${useCase.value?.cardLabel ?? 'OpenMuse video'} made with OpenMuse`)
const metaDescription = computed(() => useCase.value?.metaDescription ?? '')

useHead({
  title: fullTitle,
  link: [{ rel: 'canonical', href: canonical }],
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: 'index,follow' },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: metaDescription },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:width', content: ogImageWidth },
    { property: 'og:image:height', content: ogImageHeight },
    { property: 'og:image:alt', content: ogImageAlt },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:image', content: ogImage },
    { name: 'twitter:image:alt', content: ogImageAlt },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: computed(() => {
        const u = useCase.value
        if (!u) return ''
        const pageUrl = `${SITE_URL}${route.path}`
        const heroImage = `${SITE_URL}${u.heroPoster}`
        const schemas = [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: u.title,
            description: u.metaDescription,
            image: heroImage,
            mainEntityOfPage: pageUrl,
            author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
            publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: u.faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          // HowTo — mirrors the on-page Steps section. Google may render
          // step-by-step results in SERPs for tutorial-style queries.
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: u.title,
            description: u.metaDescription,
            image: heroImage,
            step: u.steps.map((s, idx) => ({
              '@type': 'HowToStep',
              position: idx + 1,
              name: s.title,
              text: s.body,
            })),
          },
          // BreadcrumbList — two-level (Home > <Title>). Helps Google render
          // a breadcrumb trail in the SERP instead of the raw URL.
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: u.title, item: pageUrl },
            ],
          },
        ]
        return JSON.stringify(schemas).replace(/</g, '\\u003c')
      }),
    },
  ],
})

// Through appLoginUrl (not string-concat) so a ctaQuery-bearing returnUrl gets
// URLSearchParams-encoded — an unencoded nested ?look= would bind to /login and
// silently vanish (docs/archive/VISUALIZER-UX-PLAN.md §8.8).
const primaryHref = computed(() => {
  const u = useCase.value
  // Empty segments dropped: the 'editor' section is a bare front door with no
  // slug, and `/editor/` would be a second URL for one page. Vue Router's
  // non-strict default would still match it, which is exactly why it would have
  // gone unnoticed.
  const path = `/${[u?.section ?? 'quick-ai', u?.primaryQuickMakeSlug].filter(Boolean).join('/')}`
  return appLoginUrl({ mode: 'signup', returnUrl: u?.ctaQuery ? `${path}?${u.ctaQuery}` : path })
})
const secondaryHref = computed(() =>
  useCase.value?.secondaryCta === 'quickMakeIndex'
    ? appLoginUrl({ mode: 'signup', returnUrl: '/quick-ai' })
    : appLoginUrl({ mode: 'signup' }),
)
const secondaryLabel = computed(() =>
  useCase.value?.secondaryCta === 'quickMakeIndex' ? 'See all Quick AI' : 'Or start a full project',
)

const relatedUseCases = computed(() => relatedUseCasesFor(useCase.value?.slug ?? ''))

const openFaq = ref<number | null>(null)
function toggleFaq(index: number) {
  openFaq.value = openFaq.value === index ? null : index
}

// Hover-play for the hero video (mirrors PathPickerSection's pattern).
// Two audible players now share this page — the film and the narrated
// walkthrough — and nothing stopped both playing at once. Mirrors the pattern in
// SongView.
function pauseOtherMedia(e: Event) {
  const started = e.target as HTMLMediaElement
  document.querySelectorAll<HTMLMediaElement>('video, audio').forEach((el) => {
    if (el !== started && !el.paused) el.pause()
  })
}

// Governs this page's OWN footage only — the hero loop and the film, which come
// out of the same project and always match. The walkthrough below is a screen
// recording of the app and stays 16:9 regardless.
const isLandscape = computed(() => useCase.value?.videoAspect === 'landscape')

const heroVideo = ref<HTMLVideoElement | null>(null)
// The film sits right under a hero of the same size, so before it plays it needs a
// play badge big enough to say "this one has sound" (the `ux` gate, 2026-10-17).
const filmVideo = ref<HTMLVideoElement | null>(null)
const filmStarted = ref(false)
function playHero() {
  if (!heroVideo.value) return
  void heroVideo.value.play().catch(() => { /* autoplay blocked — ignore */ })
}
function pauseHero() {
  if (!heroVideo.value) return
  heroVideo.value.pause()
  heroVideo.value.currentTime = 0
}

// Does ANY variant on this page carry a preview? The preview box is decided at
// section scope, not per card: mid-rollout a page will have three assets and one
// missing, and a short text card beside tall image cards reads as broken markup
// rather than as a choice you could pick. When nothing is declared — every page
// today, and the two guide pages permanently — no box is rendered at all and the
// section is exactly the text grid it has always been.
const anyVariantPreview = computed(() => useCase.value?.variants.some((v) => v.preview) ?? false)

// Playback for the variant grid. `preload="none"` so a card costs its poster and
// nothing more until it is actually watched. Keyed by index because a variant has
// no id — the roster is a fixed per-page array, so the index is stable.
function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const variantVideoRefs = ref<Record<number, HTMLVideoElement | null>>({})
function setVariantRef(idx: number, el: HTMLVideoElement | null) {
  variantVideoRefs.value[idx] = el
}
function playVariant(idx: number) {
  const v = variantVideoRefs.value[idx]
  if (!v) return
  // Applies to hover as well as the viewport path below. Gating only the latter
  // would leave a desktop user who has asked for less motion watching four
  // looping clips — the same half-measure this file's siblings are indicted for
  // in REFACTORING.md.
  if (prefersReducedMotion()) return
  void v.play().catch(() => { /* autoplay blocked — the poster stands in */ })
}
function pauseVariant(idx: number) {
  const v = variantVideoRefs.value[idx]
  if (!v) return
  v.pause()
  v.currentTime = 0
}

// Hover is the desktop nicety; it is NOT the mechanism. Most of this traffic is a
// phone, where there is no hover at all — and four of the ten effects that get
// previews chose VIDEO precisely because the thing being compared is the movement,
// so a frozen poster there is the exact defect this section exists to fix. On a
// no-hover pointer, a card plays while it is on screen instead. Held off under
// `prefers-reduced-motion`, where four looping clips are the last thing wanted.
let variantObserver: IntersectionObserver | null = null

// RE-REGISTERED ON EVERY SLUG CHANGE, and that is not belt-and-braces. All thirty
// pages are one route (`/use-cases/:slug`) behind a keyless `<RouterView>`, so
// tapping a related-use-case tile swaps the data WITHOUT remounting this component
// — the note at the top of this file says so, and the whole related grid is that
// path. A one-shot `onMounted` registration would snapshot the first page's cards
// forever: variant counts run 2, 3, 4 and 6, so a phone user moving from a
// four-variant page to a six-variant one would silently get no playback on the
// last two. Refs are keyed by index, so they are dropped at the same moment —
// index 2 is a different variant after the swap.
function observeVariants() {
  variantObserver?.disconnect()
  variantObserver = null
  variantVideoRefs.value = {}
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
  if (!window.matchMedia('(hover: none)').matches) return
  if (prefersReducedMotion()) return
  variantObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const idx = Number((entry.target as HTMLElement).dataset.variantIdx)
        if (Number.isNaN(idx)) continue
        if (entry.isIntersecting) playVariant(idx)
        else pauseVariant(idx)
      }
    },
    { threshold: 0.5 },
  )
  for (const el of document.querySelectorAll('[data-variant-idx]')) variantObserver.observe(el)
}

onMounted(observeVariants)
// `flush: 'post'` so the new page's cards are in the DOM before they are observed.
watch(() => route.params.slug, observeVariants, { flush: 'post' })

onBeforeUnmount(() => {
  variantObserver?.disconnect()
  variantObserver = null
})

// Hover-play for the related "Other use cases" grid.
const relatedVideoRefs = ref<Record<string, HTMLVideoElement | null>>({})
function setRelatedRef(slug: string, el: HTMLVideoElement | null) {
  relatedVideoRefs.value[slug] = el
}
function playRelated(slug: string) {
  const v = relatedVideoRefs.value[slug]
  if (!v) return
  void v.play().catch(() => { /* autoplay blocked — ignore */ })
}
function pauseRelated(slug: string) {
  const v = relatedVideoRefs.value[slug]
  if (!v) return
  v.pause()
  v.currentTime = 0
}
</script>

<template>
  <div v-if="useCase" class="bg-surface-950 min-h-screen">
    <!-- Hero -->
    <section class="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-20 px-6 sm:px-8 lg:px-10">

      <div class="relative z-10 max-w-6xl mx-auto grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        <div class="text-center md:text-left">
          <div class="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-surface-700 bg-surface-800 mb-6">
            <Sparkles class="w-3.5 h-3.5 text-primary-300" />
            <span class="text-xs font-medium text-primary-300">Use case</span>
          </div>
          <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] mb-5 text-surface-50">
            <span class="text-primary-300">{{ useCase.heroHeadline }}</span>
          </h1>
          <p class="text-base sm:text-lg text-surface-300 leading-relaxed mb-8 max-w-xl mx-auto md:mx-0">{{ useCase.heroSubline }}</p>

          <div class="flex flex-col sm:flex-row items-center md:items-start sm:items-center justify-center md:justify-start gap-3">
            <a
              :href="primaryHref"
              class="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 text-sm font-semibold text-white bg-primary-600 rounded-full shadow-lg hover:bg-primary-700 transition-colors group w-full sm:w-auto"
            >
              {{ useCase.primaryCtaLabel }}
              <ArrowRight class="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              :href="secondaryHref"
              class="inline-flex items-center gap-2 px-4 py-3 text-sm font-medium text-surface-300 hover:text-primary-300 transition-colors"
            >
              {{ secondaryLabel }}
              <ArrowRight class="w-4 h-4" />
            </a>
          </div>

          <div class="mt-6 flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1.5 text-xs sm:text-sm text-surface-400">
            <span class="flex items-center gap-1.5"><Check class="w-3.5 h-3.5 text-emerald-400" /> Free to try</span>
            <span class="flex items-center gap-1.5"><Check class="w-3.5 h-3.5 text-emerald-400" /> No watermark with any token pack</span>
            <span class="flex items-center gap-1.5"><Check class="w-3.5 h-3.5 text-emerald-400" /> Included music licensed</span>
          </div>
        </div>

        <div
          class="relative mx-auto md:ml-auto md:mr-0 w-full"
          :class="isLandscape ? 'max-w-lg' : 'max-w-xs'"
          @mouseenter="playHero"
          @mouseleave="pauseHero"
        >
          <div
            class="relative rounded-card overflow-hidden border border-surface-800 bg-black shadow-2xl"
            :class="isLandscape ? 'aspect-video' : 'aspect-[9/16]'"
          >
            <video
              ref="heroVideo"
              :src="useCase.heroVideo"
              :poster="useCase.heroPoster"
              muted
              loop
              playsinline
              preload="metadata"
              class="absolute inset-0 w-full h-full object-cover"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-surface-950/40 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </section>

    <!-- The film — the finished thing, watchable, before any instruction. A how-to
         whose output you cannot see is asking for faith; the hero above is a
         nine-second silent loop, which is a trailer, not the film.

         It sat below the problem paragraph until 2026-08-27, when the ask was that
         these pages are "too wordy" and the visual should lead. Moving it up did not
         overrule that placement so much as finish it: the comment here already
         argued "before any instruction", and a paragraph of narrative is
         instruction. Only the two guide pages carry a film, so this section is
         absent on the other twenty-eight.

         Shape comes from the entry, not from here: a portrait film is capped narrow
         because 9:16 in a wide column is a letterbox on its side, and a landscape
         one takes max-w-2xl to match the homepage's film stage. Hardcoding 9:16 put
         the 16:9 Austria film in a tall box that was half black. preload="none":
         12-21 MB stays unfetched unless someone presses play, which is also why the
         poster is not optional. -->
    <!-- border-t, because this is one of only two body sections with no heading, and
         after the move it can land directly under the hero — which declares no band of
         its own and inherits the same #0b0b0f. A band audit cannot see that adjacency;
         the `ux` gate did. Same seam idiom as AppFooter. Also covers the latent case of
         a film on a page with `variants: []`, which would put three identical bands in
         a row. -->
    <section v-if="useCase.filmVideo" class="border-t border-surface-800 py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
      <div class="max-w-3xl mx-auto text-center">
        <!-- No v-if here: the section above already carries it, and two guards on
             one condition is one of them going stale later. -->
        <figure>
          <!-- The caption leads, it does not trail. Promoted to its own section this is
               an unlabelled black rectangle on arrival, and the reader learned what it
               was only after looking at it — worst on the page whose film is portrait,
               where a second 320px video under the hero's own read as the same clip
               repeated. The homepage's FilmSection carries a heading and a lead-in for
               exactly this; moving the caption up buys the label with no new words,
               which is the constraint the user set ("keep the words"). -->
          <figcaption class="mb-3 text-sm text-surface-200">
            {{ useCase.filmCaption }}
          </figcaption>
          <div
            class="relative mx-auto w-full rounded-card overflow-hidden border border-surface-700 bg-black shadow-2xl"
            :class="isLandscape ? 'max-w-2xl' : 'max-w-sm'"
          >
            <video
              ref="filmVideo"
              :src="useCase.filmVideo"
              :poster="useCase.filmPoster ?? useCase.heroPoster"
              controls
              playsinline
              preload="none"
              aria-label="The full film, with sound"
              class="block w-full bg-black object-cover"
              :class="isLandscape ? 'aspect-video' : 'aspect-[9/16]'"
              @play="(e) => { filmStarted = true; pauseOtherMedia(e) }"
            />
            <button
              v-if="!filmStarted"
              type="button"
              aria-label="Play with sound"
              class="absolute inset-0 m-auto w-16 h-16 rounded-full bg-black/60 border border-white/30 text-white flex items-center justify-center hover:bg-black/75 transition-colors"
              @click="filmVideo?.play()"
            >
              <Play class="w-7 h-7 ml-1" fill="currentColor" />
            </button>
          </div>
        </figure>
      </div>
    </section>

    <UseCaseArticle v-if="useCase.article" :article="useCase.article" />

    <!-- Variants — "What you can pick". Mirrors the product picker so the
         landing reads honest, not aspirational.
         ABOVE the prose since 2026-08-27: the ask was that these pages are "too
         wordy" and that people take the default because they never learn the
         alternatives exist. A grid that answers that, placed below three
         paragraphs, answers it for whoever scrolls.
         Still hidden when `variants` is empty — five pages, all correctly so:
         `lego-couple-video` (Lego Kiss picks nothing but duration) and four whose
         choices already have previewed pickers inside the product. Photo Comes
         Alive is no longer among them; its page had a real 4-way motion picker and
         showed none of it. -->
    <section v-if="useCase.variants.length" class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-900">
      <div class="max-w-5xl mx-auto">
        <!-- Heading is overridable because one entry's `variants` are not pickers.
             The editor guide lists the four craft decisions that made its film work,
             and "Pick the one that fits the moment — the AI handles the rest" is the
             exact opposite of what that page argues. The sub-line is dropped with the
             default heading rather than reworded per entry: a page that renames the
             section is a page whose items explain themselves. -->
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-3 text-center">{{ useCase.variantsHeading ?? 'What you can pick' }}</h2>
        <!-- No count here: rosters range 3-4 across pages (business-jingle ships 3). -->
        <p v-if="!useCase.variantsHeading" class="text-sm sm:text-base text-surface-400 text-center max-w-xl mx-auto mb-10">Hand-tuned variants. Pick the one that fits the moment — the AI handles the rest.</p>
        <div v-else class="mb-10"></div>
        <!-- Four across at `lg` ONCE there are pictures. Two across in a 944px column makes
             each card 462 wide and ~745 tall with its prose, so the first ROW alone overflows
             a 1280x800 viewport — four choices you cannot see together are not a comparison,
             which is the one thing this section exists to be. Text-only pages keep the 2-up
             prose cards: their cards are sentences, and sentences want the width. -->
        <div
          class="grid gap-4 sm:gap-5"
          :class="anyVariantPreview ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2'"
        >
          <component
            :is="variant.href ? RouterLink : 'div'"
            v-for="(variant, idx) in useCase.variants"
            :key="idx"
            :to="variant.href"
            class="group block rounded-card border border-surface-800 bg-surface-900 overflow-hidden"
            :data-variant-idx="idx"
            @mouseenter="playVariant(idx)"
            @mouseleave="pauseVariant(idx)"
          >
            <!-- The preview is optional, so a card with none renders exactly the
                 text block this section has always shown. Since 2026-08-27 that is
                 the state of the guide pages, whose "variants" are craft decisions
                 rather than options anyone picks; the ten pages whose effect has a
                 real picker all carry pictures. -->
            <div
              v-if="anyVariantPreview"
              class="relative bg-black border-b border-surface-800"
              :class="isLandscape ? 'aspect-video' : 'aspect-[3/4]'"
              aria-hidden="true"
            >
              <!-- The resting frame is a lazy <img> BEHIND the video, not the video's own
                   `poster` attribute. `preload="none"` governs only the media resource — a
                   poster is fetched the moment the element is inserted and <video> has no lazy
                   equivalent, so four posters downloaded eagerly on a section that sits below
                   the hero AND the film: 614 KB below the fold across the four video pages,
                   while the still cards in the same grid were already doing it the lazy way.
                   PreviewTile.vue reached this conclusion first and its comment records what
                   the mistake cost there. -->
              <img
                v-if="variant.preview"
                :src="variant.preview.kind === 'video' ? variant.preview.poster : variant.preview.src"
                alt=""
                loading="lazy"
                class="absolute inset-0 w-full h-full object-cover"
              />
              <video
                v-if="variant.preview?.kind === 'video'"
                :ref="(el) => setVariantRef(idx, el as HTMLVideoElement | null)"
                :src="variant.preview.src"
                muted
                loop
                playsinline
                preload="none"
                class="absolute inset-0 w-full h-full object-cover"
              />
              <!-- Name-on-wash: this variant has no asset yet but a sibling does.
                   The choice keeps its box and stays legible rather than collapsing
                   into a short text card beside tall image cards — the same rule
                   the app's style picker follows for a missing preview. -->
              <div
                v-if="!variant.preview"
                class="absolute inset-0 flex items-end bg-gradient-to-br from-surface-800 to-surface-900 p-4"
              >
                <span class="text-sm font-medium text-surface-300">{{ variant.title }}</span>
              </div>
            </div>
            <div class="p-5 sm:p-6">
              <h3 class="text-base sm:text-lg font-semibold text-surface-50 mb-2 flex items-center gap-2.5" :class="{ 'group-hover:text-primary-300': variant.href }">
                <span class="text-xs font-bold text-primary-300 bg-surface-800 rounded-full w-6 h-6 inline-flex items-center justify-center flex-shrink-0">{{ idx + 1 }}</span>
                {{ variant.title }}
              </h3>
              <p class="text-sm text-surface-300 leading-relaxed">{{ variant.description }}</p>
            </div>
          </component>
        </div>
      </div>
    </section>

    <!-- Problem / narrative. border-t for the same reason as the film section above:
         it carries no heading, and on the five pages with no variants it now sits
         straight under the hero, which is the same #0b0b0f with nothing between them. -->
    <section class="border-t border-surface-800 py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
      <div class="max-w-3xl mx-auto">
        <p class="text-base sm:text-lg text-surface-200 leading-relaxed">{{ useCase.problem }}</p>
      </div>
    </section>

    <!-- Occasions — compact chip-grid. Broad keyword coverage per page, since
         each occasion is a distinct query intent (e.g. "Halloween pet video"
         vs "pet birthday video"). -->
    <section class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-900">
      <div class="max-w-5xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-3 text-center">Perfect for</h2>
        <p class="text-sm sm:text-base text-surface-400 text-center max-w-xl mx-auto mb-10">Some moments this fits — there are more.</p>
        <div class="grid sm:grid-cols-2 gap-3 sm:gap-4">
          <div
            v-for="(occasion, idx) in useCase.occasions"
            :key="idx"
            class="flex items-start gap-3 p-4 rounded-xl bg-surface-900 border border-surface-800"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-surface-500 mt-2 flex-shrink-0"></span>
            <div class="min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-surface-50 mb-1">{{ occasion.label }}</h3>
              <p class="text-xs sm:text-sm text-surface-300 leading-relaxed">{{ occasion.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tips — practical "how to upload the right photo" advice. Defuses
         the upload anxiety and captures "how to" long-tail queries. -->
    <section class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
      <div class="max-w-3xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-3 text-center">Tips for the best result</h2>
        <p class="text-sm sm:text-base text-surface-400 text-center mb-10">A few small things make a noticeable difference.</p>
        <ul class="space-y-5">
          <li
            v-for="(tip, idx) in useCase.tips"
            :key="idx"
            class="flex items-start gap-4"
          >
            <span class="text-2xl font-bold text-primary-300 leading-none flex-shrink-0 w-8">{{ idx + 1 }}</span>
            <div class="min-w-0 pt-1">
              <h3 class="text-base sm:text-lg font-semibold text-surface-50 mb-1">{{ tip.title }}</h3>
              <p class="text-sm sm:text-base text-surface-300 leading-relaxed">{{ tip.body }}</p>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- Steps — flat editorial layout: large numeral + quiet rule,
         no card chrome, so the items read as a labelled summary rather than
         clickable tiles. Mirrors the principle in FeaturesSection. -->
    <section class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-900">
      <div class="max-w-5xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-10 text-center">How it works</h2>
        <ol class="grid md:grid-cols-3 gap-10 md:gap-12 list-none p-0">
          <li v-for="(step, idx) in useCase.steps" :key="step.title">
            <div class="flex items-center gap-3 mb-3">
              <span class="text-4xl sm:text-5xl font-bold text-surface-300 leading-none">{{ idx + 1 }}</span>
              <span class="h-px flex-1 bg-surface-700"></span>
            </div>
            <h3 class="text-base sm:text-lg font-semibold text-surface-50 mb-2">{{ step.title }}</h3>
            <p class="text-sm text-surface-300 leading-relaxed">{{ step.body }}</p>
          </li>
        </ol>

        <!-- The same four steps, in the actual product, narrated. Controls-on and
             NOT autoplaying: it is evidence for the copy beside it, not a hero,
             and a reader who wants to scrub should be able to.
             Deliberately NOT `muted` — this one carries narration, and a muted
             player that plays silence when you press it reads as broken. That is
             also why it cannot autoplay: browsers only allow autoplay muted, so
             the choice is autoplay-and-silent or on-demand-and-audible, and for a
             how-to the words are the point.
             Renders only where a walkthrough exists — every other entry's steps
             section is unchanged. -->
        <figure v-if="useCase.walkthroughVideo" class="mt-14 mb-2">
          <div class="rounded-card overflow-hidden border border-surface-700 bg-surface-950 shadow-2xl">
            <div class="flex items-center gap-1.5 px-4 py-2.5 bg-surface-900 border-b border-surface-800">
              <span class="w-2.5 h-2.5 rounded-full bg-red-500/60"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-green-500/60"></span>
              <span class="ml-3 text-[11px] text-surface-400 truncate">app.open-muse.com</span>
            </div>
            <video
              :src="useCase.walkthroughVideo"
              :poster="useCase.walkthroughPoster"
              controls
              playsinline
              preload="none"
              aria-label="A narrated walkthrough of how this film was made"
              class="block w-full aspect-video bg-black"
              @play="pauseOtherMedia"
            />
          </div>
          <!-- Per-entry, not hardcoded: this used to read "The four steps in the
               editor … 44 seconds" for every page, which went wrong the moment a
               second walkthrough existed that covers seven steps in 1:41. -->
          <figcaption class="mt-3 text-center text-xs text-surface-400">
            {{ useCase.walkthroughCaption }}
            Every step it narrates is written out above.
          </figcaption>
        </figure>

        <div class="mt-14 text-center">
          <a
            :href="primaryHref"
            class="inline-flex items-center gap-2.5 px-7 py-3.5 text-sm font-semibold text-white bg-primary-600 rounded-full shadow-md hover:bg-primary-700 transition-colors group"
          >
            {{ useCase.primaryCtaLabel }}
            <ArrowRight class="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
      <div class="max-w-3xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-10 text-center">Frequently asked questions</h2>
        <div class="space-y-3">
          <div
            v-for="(faq, idx) in useCase.faqs"
            :key="idx"
            class="rounded-xl border border-surface-800 bg-surface-900 overflow-hidden"
          >
            <button
              @click="toggleFaq(idx)"
              class="w-full flex items-center justify-between gap-4 p-5 text-left"
            >
              <span class="text-sm sm:text-base font-medium text-surface-50">{{ faq.q }}</span>
              <ChevronDown v-if="openFaq !== idx" class="w-4 h-4 text-surface-400 flex-shrink-0" />
              <ChevronUp v-else class="w-4 h-4 text-surface-400 flex-shrink-0" />
            </button>
            <div v-show="openFaq === idx" class="px-5 pb-5 -mt-1">
              <p class="text-sm text-surface-300 leading-relaxed">{{ faq.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Related use cases (internal linking for topical clustering). Horizontal
         card layout matches the "Popular use cases" grid on /how-it-works. -->
    <section v-if="relatedUseCases.length" class="py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-900">
      <div class="max-w-5xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-10 text-center">Other ways to make it</h2>
        <div class="grid sm:grid-cols-2 gap-4 sm:gap-5">
          <RouterLink
            v-for="related in relatedUseCases"
            :key="related.slug"
            :to="`/use-cases/${related.slug}`"
            :data-related-use-case="related.slug"
            class="group relative flex items-stretch gap-4 p-3 rounded-card border border-surface-800 bg-surface-950 hover:border-primary-600 hover:bg-surface-900 transition-all"
            @mouseenter="playRelated(related.slug)"
            @mouseleave="pauseRelated(related.slug)"
          >
            <!-- Keep a uniform 9:16 rail. Most entries crop to fill it; a guide can
                 opt into containment when seeing the whole composition matters. -->
            <div class="relative w-24 sm:w-28 aspect-[9/16] rounded-xl overflow-hidden bg-black flex-shrink-0">
              <video
                :ref="(el) => setRelatedRef(related.slug, el as HTMLVideoElement | null)"
                :src="related.heroVideo"
                :poster="related.heroPoster"
                muted
                loop
                playsinline
                preload="none"
                class="absolute inset-0 w-full h-full"
                :class="related.cardMediaFit === 'contain' ? 'object-contain' : 'object-cover'"
              />
            </div>
            <div class="flex-1 min-w-0 flex flex-col justify-center py-2 pr-2">
              <h3 class="text-sm sm:text-base font-semibold text-surface-50 mb-1 group-hover:text-primary-300 transition-colors">{{ related.cardLabel }}</h3>
              <p class="text-xs sm:text-sm text-surface-300 leading-relaxed mb-2">{{ related.cardTeaser }}</p>
              <span class="inline-flex items-center gap-1 text-xs font-medium text-surface-400 group-hover:text-primary-300 group-hover:gap-1.5 transition-all">
                Read more
                <ArrowRight class="w-3 h-3" />
              </span>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="py-16 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-2xl sm:text-3xl font-bold mb-5 text-surface-50">
          Ready to
          <span class="text-primary-300">try it?</span>
        </h2>
        <p class="text-surface-300 text-base sm:text-lg mb-8 leading-relaxed">Free tokens at signup — no card needed.</p>
        <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            :href="primaryHref"
            class="inline-flex items-center justify-center gap-2.5 px-8 py-4 text-base font-semibold text-white bg-primary-600 rounded-full shadow-lg hover:bg-primary-700 transition-colors group w-full sm:w-auto max-w-sm"
          >
            {{ useCase.primaryCtaLabel }}
            <ArrowRight class="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            :href="secondaryHref"
            class="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-surface-300 hover:text-primary-300 transition-colors"
          >
            {{ secondaryLabel }}
            <ArrowRight class="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  </div>
</template>
